import { Injectable, inject } from '@angular/core';
import { PocketbaseService } from './pocketbase.service';
import { AuthService } from './auth.service';
import { CustomToastrService } from './toastr.service';
import { BillingStateService } from './billing-state.service';
import { Case, CasePreProcessed, Created_Case, User, User_request, MatchingGold, BenchmarkRun, MatchingResult } from 'src/app/utils/interfaces';
import { from } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class DataService {
  private readonly pocketbaseService = inject(PocketbaseService);
  private readonly authService = inject(AuthService);
  private readonly toast = inject(CustomToastrService);
  private readonly billingState = inject(BillingStateService);

  constructor() { }

  async updateCaseName(caseId: string, newName: string): Promise<boolean> {
    try {
      await this.pocketbaseService.getPocketBase().collection('cases').update(caseId, {
        "name": newName
      });
      return true;
    } catch (error) {
      console.error('Error updating case name:', error);
      return false;
    }
  }

  async updateRequestDisplayName(requestId: string, newName: string): Promise<boolean> {
    const trimmed = newName?.trim();
    if (!trimmed) {
      console.warn('Skipped update: display name was empty after trim');
      return false;
    }

    try {
      await this.pocketbaseService.getPocketBase()
        .collection('requests')
        .update(requestId, { display_name: trimmed });
      return true;
    } catch (error) {
      console.error('Error updating request display name:', error);
      return false;
    }
  }

  async loadFullCaseById(case_id: string): Promise<Created_Case | undefined> {
    const _full_case = await this.pocketbaseService.getPocketBase().collection('cases').getOne(case_id, {
      expand: 'processed',
    });

    if (!_full_case.expand) return; // TODO ERR

    const case_processed: CasePreProcessed = _full_case.expand['processed'] as CasePreProcessed;

    return {
      id: _full_case['id'],
      collectionId: _full_case['collectionId'],
      collectionName: _full_case['collectionName'],
      name: _full_case['name'],
      description: _full_case['description'],
      category: _full_case['category'],
      situation: _full_case['situation'],
      status: _full_case['status'],
      tags: _full_case['tags'],
      img: _full_case['img'],
      processed: case_processed,
    };
  }

  async loadCase(case_id: string): Promise<Case> {
    const record = await this.pocketbaseService.getPocketBase().collection('cases').getOne(case_id);
    return {
      id: record['id'],
      name: record['name'],
      img_url: record['img_url'],
      description: record['description'],
      category: record['category'],
      situation: record['situation'],
      sample_solution: record['sample_solution'],
      owner: record['owner']
    };
  }

  async loadCases(): Promise<Case[]> {
    if (!this.pocketbaseService.getPocketBase().authStore.model) return [];

    const record = await this.pocketbaseService.getPocketBase().collection('cases').getFullList();
    let cases_: Case[] = [];
    record.forEach(_case => {
      const case_: Case = {
        id: _case['id'],
        name: _case['name'],
        img_url: this.pocketbaseService.getPocketBase().files.getURL(_case, _case['img']),
        description: _case['description'],
        category: _case['category'],
        situation: _case['situation'],
        sample_solution: _case['sample_solution'],
        exam_duration: _case['exam_duration'],
        owner: _case['owner']
      };
      cases_.push(case_);
    });
    return cases_;
  }

  async loadUsersReuqests(): Promise<User_request[]> {
    const user = this.authService.user();
    if (!user) return [];
    const userID = user['id'];
    const record = await this.pocketbaseService.getPocketBase().collection('requests').getFullList({
      filter: `owner="${userID}"`,
      expand: 'case',
      sort: '-updated'
    });

    let requests: User_request[] = [];
    for (const _request of record) {
      if (!_request.expand || !_request.expand['case']) continue;
      const case_: Case = {
        id: _request.expand['case']['id'],
        name: _request.expand['case']['name'],
        img_url: this.pocketbaseService.getPocketBase().files.getURL(_request.expand['case'], _request.expand['case']['img']),
        description: _request.expand['case']['description'],
        category: _request.expand['case']['category'],
        situation: _request.expand['case']['situation'],
        sample_solution: _request.expand['case']['sample_solution'],
        exam_duration: _request.expand['case']['exam_duration'],
        owner: _request.expand['case']['owner'],
        feedback_mode: _request.expand['case']['feedback_mode'] || ''
      };
      const request: User_request = {
        id: _request['id'],
        owner: _request['owner'],
        editor_data: _request['editor_data_abgabe'],
        editor_freetext: _request['editor_freetext'] ?? '',
        case: case_,
        edit_route: _request['edit_route'],
        status: _request['status'],
        answer_tab0: _request['answer_tab0'],
        answer_tab1: _request['answer_tab1'],
        answer_tab2: _request['answer_tab2'],
        answer_tab3: _request['answer_tab3'],
        chunks: _request['chunks'],
        benchmark_mode: _request['benchmark_mode'],
        created: _request['created'],
        updated: _request['updated'],
        display_name: _request['display_name'],
        votum: _request['votum'],
        processed: _request['processed'],
        generate_votum: _request['generate_votum'],
        notes: _request['notes'],
        course: _request['course'] ?? null,
        organization: _request['organization'] ?? null,
        grader_review_status: _request['grader_review_status'],
        grader_review_completed_at: _request['grader_review_completed_at'],
        // Needed by PdfExportService so the correction PDF is named after the
        // uploaded Klausur (e.g. "b104948-kik.pdf") instead of the case name.
        matrikel_num: _request['matrikel_num'] ?? '',
        uploaded_pdf: _request['uploaded_pdf'] ?? ''
      };
      requests.push(request);
    }
    return requests;
  }

  async loadUsersReuqestById(requestId: string): Promise<User_request | undefined> {
    try {
      const _request = await this.pocketbaseService.getPocketBase().collection('requests').getOne(requestId, {
        expand: 'case'
      });

      let caseRecord = _request.expand?.['case'];
      if (Array.isArray(caseRecord)) {
        caseRecord = caseRecord.length > 0 ? caseRecord[0] : null;
      }

      if (!caseRecord) return undefined;

      let imgUrl = '';
      try {
        imgUrl = this.pocketbaseService.getPocketBase().files.getURL(caseRecord, caseRecord['img']);
      } catch (e) {
        console.warn('Failed to get image url', e);
      }

      const case_: Case = {
        id: caseRecord['id'],
        name: caseRecord['name'],
        img_url: imgUrl,
        description: caseRecord['description'],
        category: caseRecord['category'],
        situation: caseRecord['situation'],
        sample_solution: caseRecord['sample_solution'],
        exam_duration: caseRecord['exam_duration'],
        owner: caseRecord['owner'],
        feedback_mode: caseRecord['feedback_mode'] || '',
        anlagen: caseRecord['anlagen'] || [],
        collectionId: caseRecord['collectionId'],
        collectionName: caseRecord['collectionName']
      };

      return {
        id: _request['id'],
        owner: _request['owner'],
        editor_data: _request['editor_data_abgabe'],
        editor_freetext: _request['editor_freetext'] ?? '',
        case: case_,
        edit_route: _request['edit_route'],
        status: _request['status'],
        answer_tab0: _request['answer_tab0'],
        answer_tab1: _request['answer_tab1'],
        answer_tab2: _request['answer_tab2'],
        answer_tab3: _request['answer_tab3'],
        chunks: _request['chunks'],
        benchmark_mode: _request['benchmark_mode'],
        created: _request['created'],
        updated: _request['updated'],
        display_name: _request['display_name'],
        votum: _request['votum'] || '',
        processed: _request['processed'],
        generate_votum: _request['generate_votum'],
        notes: _request['notes'],
        course: _request['course'] ?? null,
        organization: _request['organization'] ?? null,
        grader_review_status: _request['grader_review_status'],
        grader_review_completed_at: _request['grader_review_completed_at'],
        // Needed by PdfExportService so the correction PDF is named after the
        // uploaded Klausur (e.g. "b104948-kik.pdf") instead of the case name.
        matrikel_num: _request['matrikel_num'] ?? '',
        uploaded_pdf: _request['uploaded_pdf'] ?? ''
      };
    } catch (err) {
      console.error(err);
      return undefined;
    }
  }

  async createVotumRequest(caseId: string): Promise<string | undefined> {
    const user = this.authService.user();
    if (!user) return;
    const record = await this.pocketbaseService.getPocketBase().collection('requests').create({
      "case": caseId,
      "votum": "",
    });
    return record['id'];
  }

  async createUsersReuqest(case_id: string, benchmarkMode: boolean = false) {
    const user = this.authService.user();
    if (!user) return;
    const userID = user['id'];
    const record = await this.pocketbaseService.getPocketBase().collection('requests').create({
      owner: userID,
      case: case_id,
      status: 0,
      benchmark_mode: benchmarkMode,
    });
    return record.id;
  }

  async saveEditorAbgabe(record_id: string, editor_data_abgabe: string) {
    return this.pocketbaseService.getPocketBase().collection('requests').update(record_id, {
      'editor_abgabe_backend': editor_data_abgabe
    });
  }

  async loadPDF(record_id: string) {
    const record = await this.pocketbaseService.getPocketBase().collection('requests').getOne(record_id);
    const url = this.pocketbaseService.getPocketBase().files.getURL(record, record['uploaded_pdf']);
    return url;
  }

  loadAvatar(user: User) {
    return this.pocketbaseService.getPocketBase().files.getURL(user, user['avatar']);
  }

  async loadSolutionCSV(casesProcessedID: string) {
    const record = await this.pocketbaseService.getPocketBase().collection('cases_processed').getOne(casesProcessedID);
    const url = this.pocketbaseService.getPocketBase().files.getURL(record, record['processed_overview_csv']);
    return url;
  }

  getCasesProcessed(processedId: string) {
    const pb = this.pocketbaseService.getPocketBase();
    return from(pb.collection('cases_processed').getOne(processedId));
  }

  getCase(id: string) {
    const pb = this.pocketbaseService.getPocketBase();
    return from(pb.collection('cases').getOne(id, {
      expand: 'processed'
    }));
  }

  updateCasesProcessed(processedId: string, data: any) {
    const pb = this.pocketbaseService.getPocketBase();
    return from(pb.collection('cases_processed').update(processedId, data));
  }

  async loadGutachtenCSV(requestId: string): Promise<string> {
    const pb = this.pocketbaseService.getPocketBase();
    const req = await pb.collection('requests').getOne(requestId);
    const processedId = req['processed'];
    if (!processedId) {
      throw new Error(`No processed record linked for request ${requestId}`);
    }
    const rec = await pb.collection('requests_processed').getOne(processedId);
    const csvField = 'processed_request_overview_csv';
    if (!rec[csvField]) {
      throw new Error(`Field ${csvField} is empty on processed record ${processedId}`);
    }
    return pb.files.getURL(rec, rec[csvField]);
  }

  async uploadPDF(record_id: string, file: File) {
    await this.pocketbaseService.getPocketBase().collection('requests').update(record_id, {
      'uploaded_pdf': file
    });
  }

  async deletePDF(record_id: string) {
    return this.pocketbaseService
      .getPocketBase()
      .collection('requests')
      .update(record_id, { uploaded_pdf: null });
  }

  async sendRequest(request_id: string) {
    const user = this.authService.user();
    if (!user) return;
    const pending = { request: request_id };
    try {
      const record = await this.pocketbaseService
        .getPocketBase()
        .collection('pending_requests')
        .create(pending);
      // Submission succeeded — decrement local credit count
      this.billingState.refresh();
      return record.id;
    }
    catch (err: any) {
      // Intercept billing credit exhaustion (backend 403 with error: "no_credits")
      const errorBody = err?.response?.data || err?.data || {};
      if (err?.status === 403 && errorBody?.error === 'no_credits') {
        this.billingState.refresh();
        throw err; // Re-throw so the calling component can show the modal (prevents circular dependency here)
      }
      const msg = err?.response?.data?.message
        || err?.data?.message
        || err?.message
        || 'Anfrage fehlgeschlagen';
      this.toast.showError(msg);
      throw err;
    }
  }

  async sendVotum(requestID: string) {
    const user = this.authService.user();
    if (!user) return;

    await this.pocketbaseService.getPocketBase().collection('requests').update(requestID, {
      "generate_votum": "True"
    });
  }

  async finalizePilotSubmission(requestId: string): Promise<any> {
    return await this.pocketbaseService.getPocketBase().send('/api/pilot/finalize', {
      method: 'POST',
      body: { requestId }
    });
  }

  async deleteRequest(requestId: string): Promise<boolean> {
    try {
      // Delete from PocketBase
      await this.pocketbaseService.getPocketBase().collection('requests').delete(requestId);
      return true;
    } catch (error) {
      console.error('Error deleting request:', error);
      throw error;
    }
  }

  async checkVotumStatus(requestId: string): Promise<{ isReady: boolean, votum?: any }> {
    try {
      const record = await this.pocketbaseService.getPocketBase().collection('requests').getOne(requestId);

      return {
        isReady: (record['status'] === 90 || record['status'] === 88) && record['votum'] !== null,
        votum: record['votum'] // Return the votum directly with the status
      };
    } catch (error) {
      console.error('Error checking votum status:', error);
      return { isReady: false };
    }
  }


  /**
   * Poll votum status at regular intervals until it's ready
   * @param requestId The request ID to check
   * @param onReady Callback when votum is ready
   * @param intervalMs Polling interval in milliseconds (default: 3000ms / 3 seconds)
   * @returns Function to stop polling
   */
  pollVotumStatus(
    requestId: string,
    onReady: (votum: any) => void,
    intervalMs: number = 3000
  ): () => void {
    let intervalId: any = null;
    let isPolling = true;

    const poll = async () => {
      if (!isPolling) return;

      try {
        const status = await this.checkVotumStatus(requestId);

        if (status.isReady && status.votum) {
          // Stop polling
          isPolling = false;
          if (intervalId) {
            clearInterval(intervalId);
          }

          // Call the ready callback
          onReady(status.votum);
        }
      } catch (error) {
        console.error('Error polling votum status:', error);
        // Continue polling even on error
      }
    };

    // Start polling immediately
    poll();

    // Set up interval for subsequent polls
    intervalId = setInterval(poll, intervalMs);

    // Return function to stop polling
    return () => {
      isPolling = false;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }

  async updateUserRequest(requestId: string, data: any): Promise<void> {
    const user = this.authService.user();
    if (!user) return;

    await this.pocketbaseService.getPocketBase().collection('requests').update(requestId, data);
  }

  async setEditRouteAndStatus(requestId: string, route: string, status: number) {
    return this.pocketbaseService.getPocketBase().collection('requests').update(requestId, {
      'edit_route': route,
      'status': status
    });
  }

  async saveEditor(requestId: string, data: any) {
    return this.pocketbaseService.getPocketBase().collection('requests').update(requestId, {
      'editor_data_abgabe': data
    });
  }

  /** Raw request record (used to verify a save actually persisted). */
  async loadRequestRecord(requestId: string): Promise<Record<string, any>> {
    return this.pocketbaseService.getPocketBase().collection('requests').getOne(requestId);
  }

  async saveEditorFreetext(requestId: string, text: string) {
    return this.pocketbaseService.getPocketBase().collection('requests').update(requestId, {
      'editor_freetext': text
    });
  }

  async deleteUserReq(requestId: string) {
    return this.pocketbaseService.getPocketBase().collection('requests').delete(requestId);
  }

  // Benchmark / Matching Gold Methods

  async getMatchingGold(requestId: string): Promise<MatchingGold[]> {
    const records = await this.pocketbaseService.getPocketBase().collection('matching_gold').getFullList({
      filter: `request="${requestId}"`,
    });
    return records.map(r => ({
      id: r.id,
      case: r['case'],
      request: r['request'],
      student_chunk_indices: r['student_chunk_indices'] || (r['student_chunk_index'] !== undefined ? [r['student_chunk_index']] : []),
      target_indices: r['target_indices'],
      student_text: r['student_text'],
      solution_text: r['solution_text'],
      target_type: r['target_type'],
      annotator_id: r['annotator_id'],
      created: r['created'],
      updated: r['updated']
    }));
  }

  async saveMatchingGold(data: Partial<MatchingGold>): Promise<MatchingGold> {
    let record;
    if (data.id) {
      record = await this.pocketbaseService.getPocketBase().collection('matching_gold').update(data.id, data);
    } else {
      record = await this.pocketbaseService.getPocketBase().collection('matching_gold').create(data);
    }

    // Normalize return data to match getMatchingGold structure
    return {
      id: record.id,
      case: record['case'],
      request: record['request'],
      student_chunk_indices: record['student_chunk_indices'] || (record['student_chunk_index'] !== undefined ? [record['student_chunk_index']] : []),
      target_indices: record['target_indices'],
      student_text: record['student_text'],
      solution_text: record['solution_text'],
      target_type: record['target_type'],
      annotator_id: record['annotator_id'],
      created: record['created'],
      updated: record['updated']
    };
  }

  async deleteMatchingGold(id: string): Promise<boolean> {
    try {
      await this.pocketbaseService.getPocketBase().collection('matching_gold').delete(id);
      return true;
    } catch (err) {
      console.error('Error deleting matching gold:', err);
      return false;
    }
  }

  // Dashboard Methods


  // Server-Side Evaluation Methods

  async createBenchmarkRun(version: string, caseId?: string): Promise<BenchmarkRun> {
    const data: any = {
      status: 'init',
      engine_version: version
    };
    if (caseId) {
      data.case_filter = caseId;
    }
    const record = await this.pocketbaseService.getPocketBase().collection('benchmark_runs').create(data);
    return record as unknown as BenchmarkRun;
  }

  async getBenchmarkRun(id: string): Promise<BenchmarkRun> {
    const record = await this.pocketbaseService.getPocketBase().collection('benchmark_runs').getOne(id);
    return record as unknown as BenchmarkRun;
  }

  pollBenchmarkRunStatus(
    runId: string,
    onCompleted: (run: BenchmarkRun) => void,
    intervalMs: number = 3000
  ): () => void {
    let intervalId: any = null;
    let isPolling = true;

    const poll = async () => {
      if (!isPolling) return;
      try {
        const run = await this.getBenchmarkRun(runId);
        if (run.status === 'completed' || run.status === 'error') {
          isPolling = false;
          if (intervalId) clearInterval(intervalId);
          onCompleted(run);
        }
      } catch (error) {
        console.error('Error polling benchmark run:', error);
      }
    };

    poll();
    intervalId = setInterval(poll, intervalMs);

    return () => {
      isPolling = false;
      if (intervalId) clearInterval(intervalId);
    };
  }

  async getMatchingResults(runId: string): Promise<MatchingResult[]> {
    const records = await this.pocketbaseService.getPocketBase().collection('matching_results').getFullList({
      filter: `run_id="${runId}"`,
      sort: '-created'
    });
    return records as unknown as MatchingResult[];
  }

  async getAllBenchmarkRuns(): Promise<BenchmarkRun[]> {
    const records = await this.pocketbaseService.getPocketBase().collection('benchmark_runs').getFullList();
    return records as unknown as BenchmarkRun[];
  }
}
